import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { getSupplierUrl } from '@bridebook/toolbox';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { Box, ButtonV2 as Button } from '@bridebook/ui';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { ICommonProps as SupplierCardProps } from '@bridebook/web/app-shared/components/supplier/supplier-card/supplier-card';
import SupplierInfoCard from '@bridebook/web/app-shared/components/supplier/supplier-info-card/supplier-info-card';
import { ISupplierRelation, TRefSupplier } from '@bridebook/web/app-shared/lib/supplier/types';
import { generateReflinkUrl } from '@bridebook/web/lib/reflinks';
import componentStyles from './recommended-photographer.style';
import SupplierAccordion, {
  IAccordionProps,
} from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import ContentContainer from '@bridebook/web/app-basic/components/supplier/content-container';

interface IProps {
  accordionProps: IAccordionProps;
  add: () => void;
  isMobile: boolean;
  market: Market;
  photographer: ISupplierRelation;
  remove: () => void;
  supplier: ISupplier;
  supplierCardProps: SupplierCardProps & { onClick: () => void };
}

const RecommendedPhotographer = ({
  accordionProps,
  add,
  isMobile,
  market,
  photographer,
  remove,
  supplier,
  supplierCardProps,
}: IProps) => {
  const { t } = useTranslation('supplier');
  const styles = componentStyles({
    isMobile,
  });
  const refSupplier: TRefSupplier = {
    supplierId: supplier.id,
    supplierName: supplier.name,
    viewedLocation: 'linkedSupplierProfile',
  };
  const photographerURL = useMemo(
    () => generateReflinkUrl<TRefSupplier>(getSupplierUrl(photographer ?? null), refSupplier),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [photographer],
  );

  return (
    <div>
      <SupplierAccordion
        {...accordionProps}
        name="recommended-photographer-accordion"
        text={t('supplier.recommendedPhotographer.accordion.title')}
        openStatic>
        <ContentContainer>
          <Box style={styles.wrapper}>
            <Box style={mergeStyles([styles.column, styles.findPhotographer])}>
              <Box style={styles.title}>{t('supplier.recommendedPhotographer.title')}</Box>
              <Box style={styles.description}>
                {t('supplier.recommendedPhotographer.details', {
                  photographerName: photographer.name,
                  supplierName: supplier.name,
                })}
              </Box>
              {!isMobile && (
                <Box>
                  <Link href={photographerURL} passHref>
                    <Button styleWrapper={styles.buttonWrapper}>
                      {t('supplier.recommendedPhotographer.button.viewProfile')}
                    </Button>
                  </Link>
                </Box>
              )}
            </Box>
            <Box style={mergeStyles([styles.column, styles.photographerCard])}>
              <SupplierInfoCard
                add={add}
                remove={remove}
                supplier={photographer}
                refSupplier={refSupplier}
                shortlistedLocation="supplierRecommendedPhotographer"
                minified={true}
                market={market}
                showDetailsTags={false}
                showBudgetMatch={false}
                eagerLoad={true}
                supplierTileOrder={0}
                key={`recommended-supplier-${supplier.publicId}`}
                {...supplierCardProps}
              />
            </Box>
          </Box>
        </ContentContainer>
      </SupplierAccordion>
    </div>
  );
};

export default RecommendedPhotographer;
